/**
 * UTM-захват: чтение меток трафика при входе и передача их на бэкенд.
 *
 * Метки (utm_source / utm_medium / utm_campaign / utm_term / utm_content) могут
 * прийти тремя путями:
 *   1) ОСНОВНОЙ — ссылка https://t.me/<бот>?start=<base64url>: Telegram
 *      присылает её боту командой /start, и бот сам записывает метки в БД
 *      (TG-BOT/src/startPayload.js). Здесь делать ничего не нужно — к моменту
 *      открытия приложения атрибуция уже стоит;
 *   2) Telegram, если главное мини-приложение включено:
 *      https://t.me/<бот>?startapp=<base64url> → значение приезжает в
 *      window.Telegram.WebApp.initDataUnsafe.start_param;
 *   3) Веб напрямую: https://scalpex.space/?utm_source=...&utm_medium=...
 *      → метки лежат в window.location.search.
 *
 * Отправка fire-and-forget (не блокирует загрузку) и один раз за открытие.
 * Атрибуцию (первый источник не затирается) держит БЭКЕНД (registerUtmEntry),
 * поэтому повторный POST безопасен — юзеру, который уже был «атрибутирован»,
 * обновится только время последнего визита.
 */

import { registerUtm, getPublicBotInfo } from './api'

// База ссылки на бота. ФОРМА ССЫЛКИ ЗДЕСЬ ПРИНЦИПИАЛЬНА: метки едут классическим
// deep-link'ом t.me/<бот>?start=<метка> (см. buildStartLink ниже).
//
// Почему НЕ ?startapp=… (2026-09-11). Обе «мини-апповые» формы требуют
// настройки в BotFather, которой у нас нет:
//   • t.me/<бот>/app — Direct Link Mini App: нужно создать приложение через
//     /newapp ровно с таким коротким именем;
//   • t.me/<бот>?startapp=… — главное мини-приложение: нужно включить его в
//     Bot Settings → Configure Mini App.
// Наш деплой настраивает только кнопку меню (webAppUrl.js → setChatMenuButton),
// а это другая сущность — ?startapp= она не включает. В итоге Telegram не
// открывал НИ ОДНУ такую ссылку и отвечал «бот не найден»: страдал генератор
// UTM в админке, кнопка веб-лендинга, шаблоны постов и реферальная ссылка.
//
// Классический ?start= работает у любого бота и без настроек: Telegram
// открывает чат и присылает боту команду /start <метка>, а метку принимает
// бот (TG-BOT/src/startPayload.js) — реферальную привязку и UTM он записывает
// сам, ещё до открытия приложения. Поэтому start_param приложению больше не
// нужен; чтение start_param ниже сохранено как второй путь — на случай, если
// главное мини-приложение когда-нибудь включат.
//
// ВАЖНО: это ФОЛБЭК. Основной источник имени бота — бэкенд (getBotUsername:
// username из getMe при каждом старте бота). Если бота переименуют, зашитое
// здесь имя устареет и ссылки будут давать «бот не найден» — поэтому реальные
// ссылки фронт строит через buildBotLinkBase() (ниже), а эту константу держит
// только как запасную на случай, когда бэкенд недоступен.
export const BOT_LINK_BASE = 'https://t.me/scalpexfun_bot'

// Реальное имя бота с бэкенда, полученное один раз (кеш на время сессии).
// Сеттер прокинут для компонентов, которые уже сходили в API своим способом
// (UtmSection/WebLanding зовут fetchBotUsername и сами ждут Promise).
let resolvedUsername = null

/**
 * База t.me-ссылки по РЕАЛЬНОМУ имени бота (бэкенд) или фолбэк-константе.
 * Никогда не бросает: при ошибке сети/API возвращает BOT_LINK_BASE.
 */
export async function fetchBotUsername() {
  if (resolvedUsername) return resolvedUsername
  try {
    const r = await getPublicBotInfo()
    if (r && r.username) resolvedUsername = String(r.username)
  } catch { /* сеть/сервер недоступны — остаёмся на фолбэке */ }
  return resolvedUsername || null
}

/** t.me-база по имени: username ? 'https://t.me/<username>' : фолбэк. */
export function buildBotLinkBase(username) {
  return username ? `https://t.me/${username}` : BOT_LINK_BASE
}

/**
 * Готовая ссылка на бота с меткой: https://t.me/<бот>?start=<метка>.
 * Метка — либо 'ref_<код>', либо base64url-строка из compactUtm. Обе умещаются
 * в лимит Telegram (64 символа) и состоят только из A-Za-z0-9_-, поэтому
 * percent-encoding не нужен. Без метки возвращает просто базу.
 * Имя бота берётся с бэкенда (fetchBotUsername), иначе — фолбэк-константа.
 */
export function buildStartLink(username, payload) {
  const base = buildBotLinkBase(username)
  const p = typeof payload === 'string' ? payload.trim() : ''
  return p ? `${base}?start=${p}` : base
}

// Полные ключи (utm_source=…) и КОРОТКИЕ (s=…): короткая форма нужна для
// Telegram-ссылки, где startapp ограничен 64 символами (см. compactUtm).
const UTM_KEY_ALIASES = {
  utm_source: 'source', utm_medium: 'medium', utm_campaign: 'campaign',
  utm_term: 'term', utm_content: 'content',
  s: 'source', m: 'medium', c: 'campaign', t: 'term', ct: 'content',
}

// Разбирает строку вида 's=telegram&m=partner' или полную
// 'utm_source=telegram&utm_medium=partner' в объект { source, medium, ... }.
// null — меток нет.
function parseUtmFields(s) {
  if (!s) return null
  try {
    const params = new URLSearchParams(s)
    const out = {}
    for (const [key, field] of Object.entries(UTM_KEY_ALIASES)) {
      const v = params.get(key)
      if (v && !out[field]) out[field] = v
    }
    return Object.keys(out).length ? out : null
  } catch {
    return null
  }
}

// Разбирает значение start_param из Telegram-ссылки. Приезжает base64url
// (см. compactUtm), но эта же функция обслуживает и веб-путь, где в строке
// лежит обычный 'utm_source=…' — поэтому пробуем обе формы.
function parseUtmFromString(s) {
  if (!s) return null
  // 1) Обычная строка: в ней есть '=' и '&', а в base64url их не бывает.
  const direct = parseUtmFields(s)
  if (direct) return direct
  // 2) base64url — то, что реально приезжает в start_param.
  if (/^[A-Za-z0-9_-]+$/.test(s)) {
    try { return parseUtmFields(b64urlDecode(s)) } catch { return null }
  }
  return null
}

// ── Кодировка значения startapp ─────────────────────────────────────────────
// Telegram допускает в startapp ТОЛЬКО A-Z a-z 0-9 _ - и максимум 64 символа.
// Наша компактная строка содержит '=' и '&', которых в этом наборе нет, а
// percent-encoding не спасает — символ '%' запрещён ровно так же. Раньше
// значение уходило как 's%3Dtelegram%26m%3Dpartner%26c%3Dfriend', то есть
// заведомо недопустимым, и метки до приложения не доезжали. Пакуем строку в
// base64url — так же, как это делает штатная кодировка Telegram (aiogram:
// encode=True → urlsafe base64).
export const STARTAPP_MAX = 64

function b64urlEncode(str) {
  let bin = ''
  for (const b of new TextEncoder().encode(str)) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function b64urlDecode(str) {
  const b64 = String(str).replace(/-/g, '+').replace(/_/g, '/')
  const pad = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  const bin = atob(pad)
  return new TextDecoder().decode(Uint8Array.from(bin, c => c.charCodeAt(0)))
}

// Компактная кодировка UTM для Telegram startapp. Лимит значения — 64 символа,
// полная UTM-строка (75+) в него не влезает (п.5 task.txt). Кодируем короткими
// ключами s/m/c/t/ct в base64url: сначала обязательные поля
// (source/medium/campaign), потом — пока результат влезает под лимит.
// Возвращает готовое значение для ?start=… (или ?startapp=…) либо '' без меток.
export function compactUtm({ source, medium, campaign, term, content } = {}) {
  // Значение кодируем ТОЛЬКО если в нём есть символы, ломающие разбор
  // (& = + % # или пробел) — тогда '&' и '=' внутри значения (source='a&b')
  // не разломают строку, а URLSearchParams вернёт их обратно. Всё остальное
  // отдаём как есть: кириллица и прочий UTF-8 переживают base64url без правок,
  // а percent-encoding раздул бы их втрое и съел лимит 64 символа
  // (source='партнёр' занимал 56 из 64 — campaign уже не влезал).
  const enc = v => {
    const s = (v || '').trim()
    return /[&=+%#\s]/.test(s) ? encodeURIComponent(s) : s
  }
  const fields = [['s', source], ['m', medium], ['c', campaign], ['t', term], ['ct', content]]
  const parts = []
  for (const [k, v] of fields) {
    if (!(v || '').trim()) continue
    const part = `${k}=${enc(v)}`
    const joined = parts.length ? `${parts.join('&')}&${part}` : part
    if (b64urlEncode(joined).length > STARTAPP_MAX) break
    parts.push(part)
  }
  return parts.length ? b64urlEncode(parts.join('&')) : ''
}

// Читает метки из всех доступных источников. Возвращает
// { source, medium, campaign, term, content, entryMedium: 'telegram'|'web' }
// или null, если меток нет.
export function readUtmParams() {
  let utm = null
  let entryMedium = null

  // 1) Telegram: значение startapp (start_param), если приложение открыто
  //    по ссылке t.me/<бот>?startapp=<base64url>
  const startParam = window.Telegram?.WebApp?.initDataUnsafe?.start_param
  if (startParam) {
    const p = parseUtmFromString(startParam)
    if (p) { utm = p; entryMedium = 'telegram' }
  }

  // 2) Прямое открытие веба: метки в адресной строке (?utm_source=...).
  //    Внутри Telegram (Telegram WebApp) этот путь не срабатывает — url вебвью
  //    свой, поэтому startapp выше — основной канал.
  if (!utm && window.location?.search) {
    const p = parseUtmFromString(window.location.search.slice(1))
    if (p) { utm = p; entryMedium = 'web' }
  }

  return utm ? { ...utm, entryMedium } : null
}

let captured = false

// Вызывается один раз при старте приложения (см. App.jsx). Метки считываются
// и отправляются на бэкенд; ошибки молча игнорируются — трекинг не должен
// влиять на работу приложения.
export function captureUtm() {
  if (captured) return
  const utm = readUtmParams()
  if (!utm) return
  captured = true
  const payload = {
    source:  utm.source,
    medium:  utm.medium,
    campaign: utm.campaign,
    term:    utm.term,
    content: utm.content,
    entryMedium: utm.entryMedium,
    referer: (typeof document !== 'undefined' ? document.referrer : '') || '',
  }
  registerUtm(payload)
    .then(() => {})
    .catch(() => {})
}
